import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {getFileSize} from 'src/components/image-select/getImageSize';
import {FormInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {MessageContext} from 'src/contexts/message-context';
import {SectionIconType as SectionIconTypes} from 'src/openapi/admin/models/SectionIconType';
import {SectionType as SectionTypes} from 'src/openapi/admin/models/SectionType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {DropZone} from '@pb/kit-component-dropzone';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import Label from '@pb/kit-component-label';
import Loading, {Types as LoadingTypes} from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';


import {useGetSearches} from '../../searches/useGetSearches';
import {MAX_CONTENT_SIZE} from '../constants';
import {useGetConferenceIcons} from '../hooks/useGetConferenceIcons';

import {EditSectionSkeleton} from './edit-section.skeleton';
import {useDownloadSectionContent} from './useDownloadSectionContent';
import {useEditSection} from './useEditSection';
import {useEditSectionContent} from './useEditSectionContent';


import type {SectionIconType} from 'src/openapi/admin/models/SectionIconType';
import type {SectionType} from 'src/openapi/admin/models/SectionType';
import type {CheckBoxChangeData} from 'src/typedef';

type FileWithPreview = File & {preview?: string};

const getFilePreview = (file?: File | null) => {
    if (file && typeof (file as FileWithPreview).preview === 'string') {
        return (file as FileWithPreview).preview;
    }

    return undefined;
};

type Props = {
    sectionId: number,
    conferenceId: number,
    onClose: () => void,
    onSave: () => void,
};

export const EditSectionModal = ({onSave, onClose, conferenceId, sectionId}: Props) => {
    const {setSuccessMessage} = useContext(MessageContext);

    const [busy, setBusy] = useState(false);
    const [serverError, setServerError] = useState('');

    const {
        isLoading: isSectionLoading,
        data: sectionData,
        setData: setSectionData,
        isChanged: isSectionDataChanged,
        saveChanges: updateSection,
    } = useEditSection({conferenceId, sectionId});

    const {
        isLoading: isContentLoading,
        data: contentData,
        setData: setContentData,
        isChanged: isContentChanged,
        saveChanges: updateContentAction,
        loadData: loadSectionContent,
    } = useEditSectionContent({sectionId});

    const {
        busy: downloadingBusy,
        event: downloadContentAction,
    } = useDownloadSectionContent({sectionId, conferenceId});

    const {
        data: icons,
        isLoading: isIconsLoading,
    } = useGetConferenceIcons();

    const {
        data: searches,
        isLoading: isSearchesLoading,
    } = useGetSearches({conferenceId});

    useEffect(() => {
        if (sectionData.sectionType !== SectionTypes.CUSTOM) {
            setContentData({name: '', file: null});
        } else {
            loadSectionContent();
        }
    }, [sectionData.sectionType, loadSectionContent, setContentData]);

    const saveChanges = useCallback(async () => {
        const calls = [];
        setBusy(true);
        if (isSectionDataChanged) {
            calls.push(await updateSection());
        }
        if (isContentChanged) {
            calls.push(await updateContentAction());
        }
        setBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        if (errors.length) {
            setServerError(errors.join('\n'));
        } else {
            setSuccessMessage('Section updated successfully');
            onSave();
            onClose();
        }
    }, [
        setSuccessMessage,
        isSectionDataChanged,
        updateSection,
        isContentChanged,
        updateContentAction,
        onSave,
        onClose,
    ]);

    const changeSectionName = useCallback((sectionName: string) => {
        setSectionData(prev => ({...prev, sectionName}));
    }, [setSectionData]);

    const changeWebVersionEnabled = useCallback((changedData: CheckBoxChangeData) => {
        const webVersionEnabled = changedData.event.currentTarget.checked;
        setSectionData(prev => ({...prev, webVersionEnabled}));
    }, [setSectionData]);

    const changeMobileVersionEnabled = useCallback((changedData: CheckBoxChangeData) => {
        const mobileVersionEnabled = changedData.event.currentTarget.checked;
        setSectionData(prev => ({...prev, mobileVersionEnabled}));
    }, [setSectionData]);

    const changeSectionType = useCallback((sectionType: SectionType) => {
        setSectionData(prev => ({...prev, sectionType}));
    }, [setSectionData]);

    const changeIconType = useCallback((iconType: SectionIconType) => {
        setSectionData(prev => ({...prev, iconType}));
    }, [setSectionData]);

    const changeSearches = useCallback((ids: number[]) => {
        const newSearches = searches
            .filter(({id}) => ids.includes(id))
            .map(({id, name}) => ({id, name}));
        setSectionData(prev => ({...prev, searches: newSearches}));
    }, [searches, setSectionData]);

    const changeContent = useCallback((files: File[]) => {
        const [file] = files;

        if (!file) {
            setServerError('File is corrupted or has incorrect format');
            return;
        }

        if (getFileSize(file) > 10) {
            setServerError(`File size should be less than ${MAX_CONTENT_SIZE} MB`);
            return;
        }

        setContentData({name: file.name, file});
    }, [setContentData]);

    const downloadContent = useCallback(() => {
        if (!contentData.file && !contentData.name) {
            return;
        }

        if (contentData.file) {
            const preview = getFilePreview(contentData.file);

            if (preview) {
                const anchor = document.createElement('a');
                anchor.href = preview;
                anchor.download = contentData.name;
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
            }
        } else {
            downloadContentAction();
        }
    }, [contentData, downloadContentAction]);

    const clearContent = useCallback(() => {
        setContentData({name: '', file: null});
    }, [setContentData]);

    const isUpdateDisabled = useMemo(() => {
        let disabled = false;

        if (sectionData.sectionType === SectionTypes.SEARCH && !sectionData.searches?.length) {
            disabled = true;
        }

        if (sectionData.sectionType === SectionTypes.CUSTOM && (!contentData.name && !contentData.file)) {
            disabled = true;
        }

        if (!sectionData.sectionName) {
            disabled = true;
        }

        if (!isContentChanged && !isSectionDataChanged) {
            disabled = true;
        }

        return disabled;
    }, [isSectionDataChanged, isContentChanged, sectionData, contentData]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Update Section
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {serverError ? (
                    <Message color="error" onClose={() => setServerError('')}>
                        {serverError}
                    </Message>
                ) : null}
                <Skeleton shown={isSectionLoading || isIconsLoading || isSearchesLoading} view={<EditSectionSkeleton/>}>
                    <Group direction="v">
                        <FormInput
                            label="Section Name"
                            maxLength={128}
                            width="100%"
                            value={sectionData.sectionName}
                            onBlur={changeSectionName}
                        />
                        <div>
                            <Label>Versions</Label>
                            <Group>
                                <CheckBox
                                    label="Web version enabled"
                                    checked={sectionData.webVersionEnabled}
                                    onCheckBoxChange={changeWebVersionEnabled}
                                />
                                <CheckBox
                                    label="Mobile version enabled"
                                    checked={sectionData.mobileVersionEnabled}
                                    onCheckBoxChange={changeMobileVersionEnabled}
                                />
                            </Group>
                        </div>
                        <WidthItem width="100%">
                            <Label>Section Type</Label>
                            <ComboBox
                                value={sectionData.sectionType}
                                disabled={true}
                                onChange={changeSectionType}>
                                <ComboboxItem caption="Custom" value={SectionTypes.CUSTOM}/>
                                <ComboboxItem caption="Search" value={SectionTypes.SEARCH}/>
                            </ComboBox>
                        </WidthItem>
                        <WidthItem width="100%">
                            <Label>Icon Type</Label>
                            <RadioButtons
                                name="iconType"
                                onChange={changeIconType}
                                value={sectionData.iconType}>
                                <ResponsiveElementGroup padding="0 0 16px 0">
                                    {Object.values(SectionIconTypes).map(type => (
                                        <RadioButton
                                            key={type}
                                            name={type}
                                            value={type}
                                            label={(
                                                <WidthItem width={70} key={type}>
                                                    <img
                                                        alt={type}
                                                        src={icons.find(({iconType}) => iconType === type)?.value}
                                                        width="25"
                                                    />
                                                </WidthItem>
                                            )}
                                        />
                                    ))}
                                </ResponsiveElementGroup>
                            </RadioButtons>
                        </WidthItem>
                        {sectionData.sectionType === SectionTypes.CUSTOM && (
                            <WidthItem width="100%">
                                <Skeleton shown={isContentLoading} view={<Loading fast={true}/>}>
                                    <Label>Section Content</Label>
                                    {contentData.name ? (
                                        <Group justify="space-between">
                                            <WidthItem width={300}>
                                                <EllipsisTextHint title={contentData.name}>
                                                    {contentData.name}
                                                </EllipsisTextHint>
                                            </WidthItem>
                                            <Group>
                                                {downloadingBusy ? (
                                                    <Loading type={LoadingTypes.ICON}/>
                                                ) : (
                                                    <BasicIconButton
                                                        onClick={downloadContent}
                                                        iconProps={{type: Icons.DOWNLOAD, size: 'Size_16'}}
                                                    />
                                                )}
                                                <BasicIconButton
                                                    iconProps={{type: Icons.CLOSE, size: 'Size_16'}}
                                                    onClick={clearContent}
                                                />
                                            </Group>
                                        </Group>
                                    ) : (
                                        <DropZone
                                            accept="application/pdf"
                                            mix=""
                                            multiple={false}
                                            onDrop={changeContent}>
                                            <Group direction="v">
                                                <div>Drag & drop a file to upload or <a>browse</a></div>
                                                <div className="disabled font-size font-size_footer">
                                                    Max. file size: {MAX_CONTENT_SIZE} MB. Accepted formats: PDF
                                                </div>
                                            </Group>
                                        </DropZone>
                                    )}
                                </Skeleton>
                            </WidthItem>
                        )}
                        {sectionData.sectionType === SectionTypes.SEARCH && (
                            <WidthItem width="100%">
                                <Label>Select Searches</Label>
                                <ComboBox
                                    value={sectionData.searches?.map(({id}) => id)}
                                    multiselectable={true}
                                    disabled={!searches.length}
                                    onChange={changeSearches}>
                                    {searches.map(({id, name}) => (
                                        <ComboboxItem caption={name} value={id} key={id}/>
                                    ))}
                                </ComboBox>
                            </WidthItem>
                        )}
                    </Group>
                </Skeleton>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button busy={busy} variant="Type.PRIMARY" onClick={saveChanges} disabled={isUpdateDisabled}>
                        Update section
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
